import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    FormGroup,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
    AccessTimeRounded,
    AddAlertRounded,
    CheckCircleRounded,
    ClearRounded,
    DeleteOutlineRounded,
    EditRounded,
    NotesRounded,
    NotificationsActiveRounded,
    RefreshRounded,
    ScheduleRounded,
} from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import EmbeddedLayout from "../../../components/admin/EmbeddedLayout";
import notificationsController, { INotification, INotificationPayload, NotificationTarget } from '../../../Api/notifications';
import { useUser } from '../../../hooks/useUser';

type NotificationDraft = {
    targets: NotificationTarget[];
    title: string;
    message: string;
    displayAt: string;
    expiresAt: string;
    createdBy: string;
};

type FilterKey = 'all' | 'aktywne' | 'zaplanowane' | 'wygasłe' | 'usunięte';

const formatInputDateTime = (date: Date) => {
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
};

const toInputDateTime = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return formatInputDateTime(date);
};

const shiftDays = (base: string | undefined, days: number) => {
    const origin = base ? new Date(base) : new Date();
    const next = new Date(origin.getTime());
    next.setDate(next.getDate() + days);
    return formatInputDateTime(next);
};

const emptyDraft = (author = 'admin'): NotificationDraft => {
    const now = new Date();

    return {
        targets: ['showroom', 'ufo'],
        title: '',
        message: '',
        displayAt: formatInputDateTime(now),
        expiresAt: shiftDays(now.toISOString(), 30),
        createdBy: author,
    };
};

const formatDate = (value?: string | null) => {
    if (!value) return 'Brak';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const getNotificationStatus = (notification: INotification) => {
    const now = new Date();
    if (notification.deletedAt) return 'usunięte';

    const displayAt = notification.displayAt ? new Date(notification.displayAt) : null;
    const expiresAt = notification.expiresAt ? new Date(notification.expiresAt) : null;

    if (displayAt && displayAt > now) return 'zaplanowane';
    if (expiresAt && expiresAt <= now) return 'wygasłe';

    return 'aktywne';
};

const getStatusTone = (status: FilterKey) => {
    if (status === 'aktywne') return { bg: '#eef7ee', color: '#256b35', border: '#cfe8d2' };
    if (status === 'zaplanowane') return { bg: '#eef4ff', color: '#1f4fa3', border: '#d5e2ff' };
    if (status === 'wygasłe') return { bg: '#fff6e8', color: '#a25a00', border: '#ffe0b5' };
    if (status === 'usunięte') return { bg: '#f4f4f5', color: '#5b5b63', border: '#e4e4e7' };
    return { bg: '#f8fafc', color: '#0f172a', border: '#e2e8f0' };
};

const getStatusCount = (rows: INotification[], status: FilterKey) => {
    if (status === 'all') return rows.length;
    return rows.filter((row) => getNotificationStatus(row) === status).length;
};

const getQuickExpiryLabel = (draft: NotificationDraft) => {
    if (!draft.expiresAt) return 'Bez daty wygaśnięcia';

    const start = new Date(draft.displayAt || new Date().toISOString());
    const end = new Date(draft.expiresAt);
    const diff = end.getTime() - start.getTime();
    if (Number.isNaN(diff) || diff <= 0) return 'Termin wymaga poprawy';

    const days = Math.round(diff / (1000 * 60 * 60 * 24));
    return `Widoczne przez około ${days} dni`;
};

const getActiveExpiryPreset = (draft: NotificationDraft): 7 | 30 | 90 | 'forever' | null => {
    if (!draft.expiresAt) return 'forever';
    if (!draft.displayAt) return null;

    const start = new Date(draft.displayAt);
    const end = new Date(draft.expiresAt);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

    const diffInDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 7 || diffInDays === 30 || diffInDays === 90) {
        return diffInDays;
    }

    return null;
};

const targetOptions: { key: NotificationTarget; label: string }[] = [
    { key: 'showroom', label: 'Showroom' },
    { key: 'ufo', label: 'UFO' },
];

const NotificationsPage = ({ embedded = false }: { embedded?: boolean }) => {
    const user = useUser();
    const currentAuthor = user.currentUser?.displayName || user.currentUser?.username || user.currentUser?.email || 'admin';
    const isAdmin = user.hasPermission('notifications.read');
    const authChecked = user.authReady;

    const [filter, setFilter] = useState<FilterKey>('all');
    const [rows, setRows] = useState<INotification[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dialogError, setDialogError] = useState<string | null>(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [mode, setMode] = useState<'create' | 'edit'>('create');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draft, setDraft] = useState<NotificationDraft>(() => emptyDraft(currentAuthor));
    const [selectedNotificationIds, setSelectedNotificationIds] = useState<string[]>([]);
    const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!authChecked || !isAdmin) return;

        setIsLoading(true);
        setError(null);
        const res = await notificationsController.getAllNotifications();

        if (!res.ok) {
            setRows([]);
            setError(res.message || 'Nie udało się pobrać powiadomień');
            setIsLoading(false);
            return;
        }

        setRows(res.notifications || []);
        setIsLoading(false);
    }, [authChecked, isAdmin]);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        const nextDraft = emptyDraft(currentAuthor);
        setMode('create');
        setEditingId(null);
        setDraft(nextDraft);
        setDialogError(null);
        setOpenDialog(true);
    };

    const openEdit = (notification: INotification) => {
        const nextDraft: NotificationDraft = {
            targets: notification.targets?.length ? notification.targets : ['showroom', 'ufo'],
            title: notification.title || '',
            message: notification.message || '',
            displayAt: toInputDateTime(notification.displayAt),
            expiresAt: toInputDateTime(notification.expiresAt),
            createdBy: notification.createdBy || 'admin',
        };

        setMode('edit');
        setEditingId(notification.id);
        setDraft(nextDraft);
        setDialogError(null);
        setOpenDialog(true);
    };

    const setExpiryPreset = (days: number | null) => {
        setDraft((current) => ({
            ...current,
            expiresAt: days === null ? '' : shiftDays(current.displayAt || new Date().toISOString(), days),
        }));
    };

    const toggleTarget = (target: NotificationTarget) => {
        setDraft((current) => {
            const nextTargets = current.targets.includes(target)
                ? current.targets.filter((item) => item !== target)
                : [...current.targets, target];

            return {
                ...current,
                targets: nextTargets,
            };
        });
    };

    const save = async () => {
        const title = draft.title.trim();
        const message = draft.message.trim();

        if (draft.targets.length === 0) {
            setDialogError('Wybierz co najmniej jeden kanał powiadomienia');
            return;
        }

        if (!title || !message) {
            setDialogError('Podaj tytuł i treść powiadomienia');
            return;
        }

        if (draft.displayAt && draft.expiresAt && new Date(draft.expiresAt) <= new Date(draft.displayAt)) {
            setDialogError('Data wygaśnięcia musi być późniejsza niż data publikacji');
            return;
        }

        setIsLoading(true);
        setDialogError(null);

        const createdBy = mode === 'create' ? currentAuthor : (draft.createdBy.trim() || currentAuthor);

        const payload: INotificationPayload = {
            targets: draft.targets,
            title,
            message,
            displayAt: draft.displayAt ? new Date(draft.displayAt).toISOString() : undefined,
            expiresAt: draft.expiresAt ? new Date(draft.expiresAt).toISOString() : null,
            createdBy,
        };

        if (mode === 'create') {
            const res = await notificationsController.createNotification(payload);
            if (!res.ok) {
                setDialogError(res.message || 'Nie udało się dodać powiadomienia');
                setIsLoading(false);
                return;
            }

            setOpenDialog(false);
            await load();
            return;
        }

        if (!editingId) {
            setDialogError('Brak ID powiadomienia do edycji');
            setIsLoading(false);
            return;
        }

        const res = await notificationsController.updateNotification(editingId, payload);
        if (!res.ok) {
            setDialogError(res.message || 'Nie udało się zaktualizować powiadomienia');
            setIsLoading(false);
            return;
        }

        setOpenDialog(false);
        await load();
    };

    const remove = async (notification: INotification) => {
        const confirmed = window.confirm(`Usunąć powiadomienie "${notification.title}"?`);
        if (!confirmed) return;

        setIsLoading(true);
        setError(null);
        const res = await notificationsController.deleteNotification(notification.id);
        if (!res.ok) {
            setError(res.message || 'Nie udało się usunąć powiadomienia');
            setIsLoading(false);
            return;
        }

        const deletedAt = res.notification?.deletedAt || new Date().toISOString();

        setRows((current) => current.map((row) => (
            row.id === notification.id
                ? { ...row, deletedAt }
                : row
        )));
        setSelectedNotificationIds((current) => current.filter((id) => id !== notification.id));
        setSelectionAnchorId((current) => current === notification.id ? null : current);
        setIsLoading(false);
    };

    const stats = useMemo(() => {
        const active = rows.filter((row) => getNotificationStatus(row) === 'aktywne').length;
        const scheduled = rows.filter((row) => getNotificationStatus(row) === 'zaplanowane').length;
        const expired = rows.filter((row) => getNotificationStatus(row) === 'wygasłe').length;
        const deleted = rows.filter((row) => getNotificationStatus(row) === 'usunięte').length;

        return { active, scheduled, expired, deleted, total: rows.length };
    }, [rows]);

    const filteredRows = useMemo(() => {
        if (filter === 'all') return rows;
        return rows.filter((row) => getNotificationStatus(row) === filter);
    }, [filter, rows]);
    const filteredRowIds = useMemo(() => filteredRows.map((row) => row.id), [filteredRows]);

    useEffect(() => {
        setSelectedNotificationIds((current) => current.filter((id) => filteredRowIds.includes(id)));
        setSelectionAnchorId((current) => current && filteredRowIds.includes(current) ? current : null);
    }, [filteredRowIds]);

    const draftPreviewStatus = useMemo(() => {
        const notification: INotification = {
            id: '',
            type: 'manual',
            targets: draft.targets,
            title: draft.title,
            message: draft.message,
            displayAt: draft.displayAt ? new Date(draft.displayAt).toISOString() : new Date().toISOString(),
            expiresAt: draft.expiresAt ? new Date(draft.expiresAt).toISOString() : null,
            deletedAt: null,
        };
        return getNotificationStatus(notification);
    }, [draft]);

    const activeExpiryPreset = useMemo(() => getActiveExpiryPreset(draft), [draft]);

    const handleSelectionModelChange = (nextSelection: readonly (string | number)[]) => {
        const nextIds = nextSelection.map((value) => String(value));
        setSelectedNotificationIds(nextIds);
        if (nextIds.length > 0) {
            setSelectionAnchorId(nextIds[nextIds.length - 1]);
        }
    };

    const handleNotificationCellClick = (rowId: string, field: string, event: React.MouseEvent) => {
        if (field === 'actions' || field === '__check__') return;

        if (event.shiftKey && selectionAnchorId) {
            const anchorIndex = filteredRowIds.indexOf(selectionAnchorId);
            const rowIndex = filteredRowIds.indexOf(rowId);

            if (anchorIndex >= 0 && rowIndex >= 0) {
                const start = Math.min(anchorIndex, rowIndex);
                const end = Math.max(anchorIndex, rowIndex);
                setSelectedNotificationIds(filteredRowIds.slice(start, end + 1));
                return;
            }
        }

        if (event.ctrlKey || event.metaKey) {
            setSelectedNotificationIds((current) => {
                const next = current.includes(rowId)
                    ? current.filter((id) => id !== rowId)
                    : [...current, rowId];

                return next;
            });
            setSelectionAnchorId(rowId);
            return;
        }

        setSelectedNotificationIds([rowId]);
        setSelectionAnchorId(rowId);
    };

    const columns = useMemo<GridColDef<INotification>[]>(() => [
        {
            field: 'title',
            headerName: 'Tytuł',
            minWidth: 220,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ py: 1 }}>
                    <Typography sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                        {params.row.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {params.row.createdBy || 'admin'}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'message',
            headerName: 'Treść',
            minWidth: 320,
            flex: 1.7,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        color: '#334155',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.45,
                    }}
                >
                    {params.row.message}
                </Typography>
            ),
        },
        {
            field: 'targets',
            headerName: 'Kanały',
            minWidth: 180,
            flex: 0.75,
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ py: 1 }}>
                    {(params.row.targets?.length ? params.row.targets : ['showroom', 'ufo']).map((target) => (
                        <Chip
                            key={target}
                            size="small"
                            variant="outlined"
                            label={target === 'showroom' ? 'Showroom' : 'UFO'}
                            sx={{ fontWeight: 700 }}
                        />
                    ))}
                </Stack>
            ),
        },
        {
            field: 'displayAt',
            headerName: 'Pokaż od',
            minWidth: 170,
            flex: 0.75,
            renderCell: (params) => (
                <Stack justifyContent="center" sx={{ height: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatDate(params.row.displayAt)}</Typography>
                    <Typography variant="caption" color="text.secondary">start</Typography>
                </Stack>
            ),
        },
        {
            field: 'expiresAt',
            headerName: 'Wygasa',
            minWidth: 170,
            flex: 0.75,
            renderCell: (params) => (
                <Stack justifyContent="center" sx={{ height: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatDate(params.row.expiresAt)}</Typography>
                    <Typography variant="caption" color="text.secondary">koniec emisji</Typography>
                </Stack>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 140,
            flex: 0.65,
            sortable: false,
            renderCell: (params) => {
                const status = getNotificationStatus(params.row);
                const tone = getStatusTone(status);

                return (
                    <Chip
                        size="small"
                        label={status}
                        variant="filled"
                        sx={{
                            fontWeight: 800,
                            bgcolor: tone.bg,
                            color: tone.color,
                            border: '1px solid',
                            borderColor: tone.border,
                            textTransform: 'capitalize',
                        }}
                    />
                );
            }
        },
        {
            field: 'actions',
            headerName: 'Akcje',
            minWidth: 110,
            flex: 0.45,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Stack direction="row" gap={0.5} alignItems="center" sx={{ height: '100%' }}>
                    <Tooltip title="Edytuj">
                        <IconButton size="small" onClick={() => openEdit(params.row)} sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <EditRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Usuń">
                        <IconButton size="small" color="error" onClick={() => remove(params.row)} sx={{ border: '1px solid', borderColor: 'rgba(211, 47, 47, 0.24)' }}>
                            <DeleteOutlineRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        },
    ], []);

    if (!authChecked) {
        return (
            <EmbeddedLayout embedded={embedded} title="Powiadomienia">
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <CircularProgress size={22} />
                        <Typography>Sprawdzam autoryzację…</Typography>
                    </Stack>
                </Paper>
            </EmbeddedLayout>
        );
    }

    if (!isAdmin) return <Navigate to="/logowanie" replace />;

    return (
        <EmbeddedLayout embedded={embedded} title="Powiadomienia">
            <Box sx={{ maxWidth: 1480, mx: 'auto', px: { xs: 1, sm: 1.5, lg: 2 }, py: { xs: 1, md: 2 } }}>
                <Stack gap={1.5}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 1.5, md: 1.75 },
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
                        }}
                    >
                        <Stack direction={{ xs: 'column', xl: 'row' }} justifyContent="space-between" gap={1.5}>
                            <Box>
                                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Box sx={{ width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#edf2ff', color: '#1d4ed8' }}>
                                        <NotificationsActiveRounded fontSize="small" />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Powiadomienia
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Panel do zarządzania komunikatami. Nowe powiadomienie domyślnie startuje teraz i ma sugerowaną ważność 30 dni.
                                </Typography>
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                                <Button variant="contained" startIcon={<AddAlertRounded />} onClick={openCreate} sx={{ borderRadius: 2, fontWeight: 800 }}>
                                    Dodaj powiadomienie
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshRounded />}
                                    onClick={load}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                >
                                    Odśwież
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>

                    <Stack direction={{ xs: 'column', lg: 'row' }} gap={1.25}>
                        {[
                            { label: 'Wszystkie', key: 'all' as FilterKey, value: stats.total, icon: <NotesRounded fontSize="small" /> },
                            { label: 'Aktywne', key: 'aktywne' as FilterKey, value: stats.active, icon: <CheckCircleRounded fontSize="small" /> },
                            { label: 'Zaplanowane', key: 'zaplanowane' as FilterKey, value: stats.scheduled, icon: <ScheduleRounded fontSize="small" /> },
                            { label: 'Wygasłe', key: 'wygasłe' as FilterKey, value: stats.expired, icon: <AccessTimeRounded fontSize="small" /> },
                            { label: 'Usunięte', key: 'usunięte' as FilterKey, value: stats.deleted, icon: <ClearRounded fontSize="small" /> },
                        ].map((item) => {
                            const selected = filter === item.key;
                            const tone = getStatusTone(item.key);
                            return (
                                <Paper
                                    key={item.label}
                                    elevation={0}
                                    onClick={() => setFilter(item.key)}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        p: 1.25,
                                        borderRadius: 2.5,
                                        border: '1px solid',
                                        borderColor: selected ? tone.border : 'divider',
                                        bgcolor: selected ? tone.bg : 'background.paper',
                                        cursor: 'pointer',
                                        transition: '0.18s ease',
                                        '&:hover': { borderColor: tone.border, bgcolor: tone.bg },
                                    }}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                                                {item.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: 26, lineHeight: 1.05, fontWeight: 900, color: '#0f172a' }}>
                                                {item.value}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: selected ? tone.color : '#64748b' }}>{item.icon}</Box>
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Stack>

                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                        <Stack
                            direction={{ xs: 'column', lg: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ lg: 'center' }}
                            gap={1.25}
                            sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fcfdff' }}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: 900, color: '#0f172a' }}>
                                    Lista powiadomień
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Widok: {filter === 'all' ? 'wszystkie statusy' : filter}. Na liście: {filteredRows.length}. Zaznaczanie: klik, Ctrl i Shift.
                                </Typography>
                            </Box>

                            <Stack direction="row" gap={0.75} flexWrap="wrap">
                                {(['all', 'aktywne', 'zaplanowane', 'wygasłe', 'usunięte'] as FilterKey[]).map((status) => (
                                    <Chip
                                        key={status}
                                        label={status === 'all' ? `wszystkie (${getStatusCount(rows, status)})` : `${status} (${getStatusCount(rows, status)})`}
                                        clickable
                                        size="small"
                                        variant={filter === status ? 'filled' : 'outlined'}
                                        onClick={() => setFilter(status)}
                                        sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                                    />
                                ))}
                            </Stack>
                        </Stack>

                        {error ? (
                            <Box sx={{ px: 1.5, pt: 1.25 }}>
                                <Alert severity="error">{error}</Alert>
                            </Box>
                        ) : null}

                        {filteredRows.length === 0 && !isLoading ? (
                            <Box sx={{ px: 2, py: { xs: 4, md: 6 } }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        maxWidth: 640,
                                        mx: 'auto',
                                        p: { xs: 2, md: 2.5 },
                                        borderRadius: 3,
                                        border: '1px dashed',
                                        borderColor: '#cbd5e1',
                                        bgcolor: '#f8fbff',
                                    }}
                                >
                                    <Stack alignItems="center" textAlign="center" gap={1.25}>
                                        <Box sx={{ width: 52, height: 52, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: '#e8f0ff', color: '#2563eb' }}>
                                            <NotificationsActiveRounded />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                                                Brak powiadomień w tym widoku
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Zamiast pustej tabeli zacznij od dodania pierwszego komunikatu albo zmień filtr statusu, jeśli szukasz archiwalnych wpisów.
                                            </Typography>
                                        </Box>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                                            <Button variant="contained" startIcon={<AddAlertRounded />} onClick={openCreate} sx={{ borderRadius: 2, fontWeight: 800 }}>
                                                Dodaj pierwsze powiadomienie
                                            </Button>
                                            <Button variant="outlined" onClick={() => setFilter('all')} sx={{ borderRadius: 2, fontWeight: 700 }}>
                                                Pokaż wszystkie
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            </Box>
                        ) : (
                            <Box sx={{ height: 640, width: '100%' }}>
                                <DataGrid
                                    rows={filteredRows}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    loading={isLoading}
                                    checkboxSelection
                                    rowSelectionModel={selectedNotificationIds}
                                    onRowSelectionModelChange={handleSelectionModelChange}
                                    onCellClick={(params, event) => handleNotificationCellClick(String(params.id), params.field, event as React.MouseEvent)}
                                    getRowHeight={() => 74}
                                    disableRowSelectionOnClick
                                    pageSizeOptions={[10, 25, 50]}
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 10, page: 0 } },
                                    }}
                                    sx={{
                                        border: 0,
                                        '& .MuiDataGrid-columnHeaders': {
                                            backgroundColor: '#f8fafc',
                                            fontWeight: 800,
                                        },
                                        '& .MuiDataGrid-cell': {
                                            borderColor: '#f1f5f9',
                                        },
                                        '& .MuiDataGrid-row:hover': {
                                            backgroundColor: 'rgba(15, 23, 42, 0.025)',
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Paper>
                </Stack>
            </Box>

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
            >
                <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                        <Stack direction="row" alignItems="center" gap={1.25}>
                            <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: '#fff' }}>
                                <NotificationsActiveRounded fontSize="small" />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                                    {mode === 'create' ? 'Nowe powiadomienie' : 'Edytuj powiadomienie'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Wpisz komunikat i określ, gdzie oraz kiedy ma się pojawić.
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton aria-label="Zamknij formularz" onClick={() => setOpenDialog(false)}>
                            <ClearRounded />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: '24px !important' }}>
                    {dialogError ? <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert> : null}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.3fr) minmax(300px, 0.7fr)' }, gap: 2.5 }}>
                        <Stack gap={2}>
                            <Box>
                                <Typography sx={{ fontWeight: 900, color: '#0f172a', mb: 0.25 }}>Treść komunikatu</Typography>
                                <Typography variant="body2" color="text.secondary">To zobaczą odbiorcy powiadomienia.</Typography>
                            </Box>
                            <TextField
                                label="Tytuł"
                                value={draft.title}
                                onChange={(e) => setDraft((current) => ({ ...current, title: e.target.value }))}
                                fullWidth
                                autoFocus
                                placeholder="Np. Przerwa techniczna w piątek"
                            />
                            <TextField
                                label="Treść"
                                value={draft.message}
                                onChange={(e) => setDraft((current) => ({ ...current, message: e.target.value }))}
                                fullWidth
                                multiline
                                minRows={7}
                                placeholder="Napisz krótko, co się wydarzy, kiedy i czy użytkownik musi coś zrobić."
                            />
                        </Stack>

                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: '#f8fafc' }}>
                            <Stack gap={1.75}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 900, color: '#0f172a' }}>Publikacja</Typography>
                                        <Typography variant="caption" color="text.secondary">Odbiorcy i czas wyświetlania</Typography>
                                    </Box>
                                    <Chip size="small" color="primary" variant="outlined" label={draftPreviewStatus} sx={{ fontWeight: 800, textTransform: 'capitalize' }} />
                                </Stack>

                                <Divider />

                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 800, color: '#475569' }}>WYŚWIETLAJ W</Typography>
                                    <FormGroup row sx={{ gap: 0.75 }}>
                                        {targetOptions.map((option) => {
                                            const selected = draft.targets.includes(option.key);
                                            return (
                                                <FormControlLabel
                                                    key={option.key}
                                                    onChange={() => toggleTarget(option.key)}
                                                    control={<Checkbox checked={selected} size="small" sx={{ p: 0.5 }} />}
                                                    label={option.label}
                                                    sx={{
                                                        m: 0,
                                                        flex: 1,
                                                        minWidth: 115,
                                                        px: 1,
                                                        py: 0.65,
                                                        borderRadius: 2,
                                                        border: '1px solid',
                                                        borderColor: selected ? 'primary.main' : 'divider',
                                                        bgcolor: selected ? 'rgba(25, 118, 210, 0.06)' : '#fff',
                                                        '& .MuiFormControlLabel-label': { fontWeight: 800, fontSize: 14 },
                                                    }}
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                </Box>

                                <TextField
                                    label="Pokaż od"
                                    type="datetime-local"
                                    value={draft.displayAt}
                                    onChange={(e) => setDraft((current) => ({ ...current, displayAt: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    label="Wygasa"
                                    type="datetime-local"
                                    value={draft.expiresAt}
                                    onChange={(e) => setDraft((current) => ({ ...current, expiresAt: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    size="small"
                                    helperText={getQuickExpiryLabel(draft)}
                                />

                                <Stack direction="row" gap={0.75} flexWrap="wrap">
                                    {([7, 30, 90] as const).map((days) => (
                                        <Chip
                                            key={days}
                                            label={`+${days} dni`}
                                            clickable
                                            size="small"
                                            color={activeExpiryPreset === days ? 'primary' : 'default'}
                                            variant={activeExpiryPreset === days ? 'filled' : 'outlined'}
                                            onClick={() => setExpiryPreset(days)}
                                        />
                                    ))}
                                    <Chip
                                        label="Bez końca"
                                        clickable
                                        size="small"
                                        color={activeExpiryPreset === 'forever' ? 'primary' : 'default'}
                                        variant={activeExpiryPreset === 'forever' ? 'filled' : 'outlined'}
                                        onClick={() => setExpiryPreset(null)}
                                    />
                                </Stack>

                                <Box sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: '#fff', border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" color="text.secondary">Przesłane przez</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {mode === 'create' ? currentAuthor : draft.createdBy}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fafcff' }}>
                    <Button onClick={() => setOpenDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
                        Anuluj
                    </Button>
                    <Button onClick={save} variant="contained" disabled={isLoading} sx={{ borderRadius: 2, fontWeight: 800 }}>
                        {mode === 'create' ? 'Dodaj powiadomienie' : 'Zapisz zmiany'}
                    </Button>
                </DialogActions>
            </Dialog>
        </EmbeddedLayout>
    )
}

export default NotificationsPage;
